export interface IGiftBudget {
  enabled: boolean;
}

export interface IMarketplaceCatalogItem {
  product_id: string;
  name: string;
  image_url?: string;
  vendor?: string;
  supplierCode?: string;
  supplierName?: string;
  creditPrice: number;
  available?: boolean;
}

export interface IMarketplaceVariant {
  variant_id: string;
  name: string;
  image_url?: string;
  options?: Record<string, string>;
  // Within the sender's budget. Out-of-pool variants still arrive so the recipient can see the
  // option exists; they carry no price.
  inPool: boolean;
  drawsFromWallet: boolean;
  preselected: boolean;
  creditPrice?: number;
  isOutOfStock: boolean;
}

export interface IMarketplaceProductDetails {
  product_id: string;
  name: string;
  description?: string;
  images?: string[];
  vendor?: string;
  supplierCode?: string;
  supplierName?: string;
  variants: IMarketplaceVariant[];
  preselected: boolean;
  // The "from" price: cheapest variant in the pool, matching the catalog card.
  creditPrice?: number;
}

export interface IVariantOptionValue {
  label: string;
  isOut: boolean;
}

export interface IVariantOptionRow {
  name: string;
  values: IVariantOptionValue[];
}

export interface IMarketplaceFilterOption {
  value: string;
  label: string;
}

export enum MarketplaceFilterKindEnum {
  Categories = 'categories',
  Brands = 'brands',
  Collections = 'collections',
}

export type TMarketplaceFilterValues = Record<MarketplaceFilterKindEnum, string[]>;

export interface IMarketplaceMetadata {
  categories: IMarketplaceFilterOption[];
  brands: IMarketplaceFilterOption[];
  collections: IMarketplaceFilterOption[];
}

export interface IMarketplaceSelectedFilter extends IMarketplaceFilterOption {
  kind: MarketplaceFilterKindEnum;
}

// Wire shapes (.claude/tasks/CLAIM_PAGE_MARKETPLACE.md): rows are normalized before they reach
// the store. cache_value is only present when the one sent was missing or unusable.
export interface IMarketplaceCatalogRequestBody extends TMarketplaceFilterValues {
  search_query: string;
  // null asks for the full wallet.
  remaining_budget: number | null;
  page_size: number;
  cursor?: string;
}

export interface IMarketplaceCatalogResponse {
  items: Record<string, unknown>[];
  has_more?: boolean;
  next_cursor?: string | null;
  cache_value?: string;
}

export interface IMarketplaceMetadataResponse extends Partial<IMarketplaceMetadata> {
  cache_value?: string;
}

export interface IMarketplaceProductDetailsResponse {
  product: Record<string, unknown>;
  cache_value?: string;
}

// quantity is always 1 in v1; the fit check carries it so raising it stays a data change.
export interface IMarketplaceCartLine {
  product_id: string;
  variant_id: string;
  creditPrice: number;
  quantity: number;
  name: string;
  vendor?: string;
  supplierCode?: string;
  supplierName?: string;
  image_url?: string;
  variantLabel?: string;
  variantName?: string;
}

export type TMarketplaceSubmitLine = Pick<IMarketplaceCartLine, 'product_id' | 'variant_id'>;

export interface ISupplierLock {
  code: string;
  name?: string;
}

export interface ICartRemovalResult {
  cart: IMarketplaceCartLine[];
  swapped: IMarketplaceCartLine | null;
  removed: IMarketplaceCartLine | null;
}

// ORed within a kind, ANDed across kinds.
export interface IMarketplaceFilters extends TMarketplaceFilterValues {
  query: string;
  fitsBudget: boolean;
}

export interface IMarketplaceCatalogContext extends TMarketplaceFilterValues {
  query: string;
  budget: number | null;
}

export enum MarketplaceDetailsStatusEnum {
  Idle = 'idle',
  Loading = 'loading',
  Loaded = 'loaded',
  Failed = 'failed',
}

export enum ClaimSourceEnum {
  None = 'none',
  Catalog = 'catalog',
  MarketplacePreselected = 'marketplace_preselected',
  MarketplaceBudget = 'marketplace_budget',
}

export enum ShipsFromNoteVariantEnum {
  Vendor = 'vendor',
  AllPartners = 'all_partners',
  Brands = 'brands',
}
